// @ts-nocheck
/**
 * ProgressScreen - 収集進捗画面
 *
 * コレクションの進捗状況を各種切り口で表示する
 */

import React, { useMemo } from 'react';
import { useCardDex } from '../../hooks/useCardDex';
// useIsMobile は現在未使用（auto-fit グリッドでレスポンシブ対応済み）
// 将来的にモバイル固有の調整が必要な場合に使用
// import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  progressStyles,
  statsCardStyles,
  filterStyles,
  COLORS,
} from '../../styles/cardDexStyles';
import { ATTRIBUTE_COLORS } from '../../../../utils/constants';
import { useCardData } from '../CardDexScreen';
import { RARITY_COLORS } from '../../../data/constants';

// ========================================
// 進捗バーコンポーネント
// ========================================

const ProgressBar = ({ label, current, total, color }) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div style={progressStyles.container}>
      <div style={progressStyles.label}>
        <span style={progressStyles.labelName}>{label}</span>
        <span style={progressStyles.labelValue}>
          {current} / {total}
        </span>
      </div>
      <div style={progressStyles.barContainer}>
        <div
          style={{
            ...progressStyles.barFill,
            width: `${percentage}%`,
            background: color
              ? `linear-gradient(90deg, ${color} 0%, ${color}88 100%)`
              : progressStyles.barFill.background,
          }}
        />
      </div>
      <div style={progressStyles.percentage}>{percentage}%</div>
    </div>
  );
};

// ========================================
// 統計カード
// ========================================

const StatCard = ({ title, value, subValue }) => (
  <div style={statsCardStyles.card}>
    <div style={statsCardStyles.title}>{title}</div>
    <div style={statsCardStyles.value}>{value}</div>
    {subValue && <div style={statsCardStyles.subValue}>{subValue}</div>}
  </div>
);

// ========================================
// モード切替
// ========================================

const ModeSwitch = () => {
  const { settings, toggleProgressMode } = useCardDex();

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>表示モード:</span>
      <button
        onClick={toggleProgressMode}
        style={{
          ...filterStyles.filterButton,
          ...(settings.progressMode === 'species' ? filterStyles.filterButtonActive : {}),
        }}
      >
        カード種別
      </button>
      <button
        onClick={toggleProgressMode}
        style={{
          ...filterStyles.filterButton,
          ...(settings.progressMode === 'all' ? filterStyles.filterButtonActive : {}),
        }}
      >
        全レアリティ
      </button>
    </div>
  );
};

// ========================================
// メイン画面
// ========================================

const ProgressScreen = () => {
  const { settings } = useCardDex();
  const { allCards, collection } = useCardData();
  const totalCardCount = allCards.length;

  // 全レアリティ数（GRは禁忌カードのみ）
  const totalWithRarities = useMemo(() => {
    // 通常レアリティ: 433 × 9 = 3,897
    // GRレアリティ: 禁忌カード数（Sティアのみ）
    const forbiddenCount = allCards.filter((c) => c.isForbidden).length;
    return totalCardCount * 9 + forbiddenCount;
  }, [allCards, totalCardCount]);

  // プレイヤーの所持カード情報
  const collectionStats = useMemo(() => {

    // ユニークカード数
    const uniqueCardIds = new Set(collection.map((e) => e.cardId));
    const ownedSpeciesCount = uniqueCardIds.size;

    // 全レアリティ数
    const ownedTotalCount = collection.length;

    // 属性別
    const attributeStats = {};
    const attributes = ['炎', '水', '光', '闇', '原始', '未来', 'なし'];
    attributes.forEach((attr) => {
      const attrCards = allCards.filter((c) => c.attribute === attr);
      const ownedAttrCards = attrCards.filter((c) => uniqueCardIds.has(c.id));
      attributeStats[attr] = {
        total: attrCards.length,
        owned: ownedAttrCards.length,
      };
    });

    // 種類別（CSVのtype値は英語）
    const typeStats = {};
    const types = [
      { key: 'monster', label: 'モンスター' },
      { key: 'magic', label: '魔法' },
      { key: 'field', label: 'フィールド' },
      { key: 'phasecard', label: 'フェイズ' },
    ];
    types.forEach(({ key, label }) => {
      const typeCards = allCards.filter((c) => c.type === key);
      const ownedTypeCards = typeCards.filter((c) => uniqueCardIds.has(c.id));
      typeStats[label] = {
        total: typeCards.length,
        owned: ownedTypeCards.length,
      };
    });

    // レアリティ別
    const rarityStats = {};
    const rarities = ['C', 'UC', 'R', 'SR', 'UR', 'HR', 'SEC', 'ALT', 'SP', 'GR'];
    rarities.forEach((rarity) => {
      const rarityEntries = collection.filter((e) => e.rarity === rarity);
      const uniqueRarityCards = new Set(rarityEntries.map((e) => e.cardId));
      // GRは禁忌カードのみ対象
      const totalForRarity =
        rarity === 'GR' ? allCards.filter((c) => c.isForbidden).length : totalCardCount;
      rarityStats[rarity] = {
        total: totalForRarity,
        owned: uniqueRarityCards.size,
      };
    });

    return {
      ownedSpeciesCount,
      ownedTotalCount,
      attributeStats,
      typeStats,
      rarityStats,
    };
  }, [allCards, totalCardCount, collection]);

  const isSpeciesMode = settings.progressMode === 'species';

  return (
    <div>
      {/* モード切替 */}
      <ModeSwitch />

      {/* 総合統計 */}
      <div style={statsCardStyles.grid}>
        <StatCard
          title="カード種別"
          value={`${collectionStats.ownedSpeciesCount} / ${totalCardCount}`}
          subValue={`${Math.round((collectionStats.ownedSpeciesCount / totalCardCount) * 100)}%`}
        />
        <StatCard
          title="全レアリティ込"
          value={`${collectionStats.ownedTotalCount}`}
          subValue={`/ ${totalWithRarities}`}
        />
      </div>

      {/* 総合進捗バー */}
      <div
        style={{
          marginBottom: '24px',
          padding: '16px',
          background: 'rgba(20, 20, 40, 0.6)',
          borderRadius: '12px',
        }}
      >
        <div style={{ fontSize: '16px', color: COLORS.textGold, marginBottom: '12px' }}>
          総合進捗
        </div>
        <ProgressBar
          label={isSpeciesMode ? 'カード種別' : '全レアリティ込'}
          current={isSpeciesMode ? collectionStats.ownedSpeciesCount : collectionStats.ownedTotalCount}
          total={isSpeciesMode ? totalCardCount : totalWithRarities}
        />
      </div>

      {/* 属性別進捗 */}
      <div
        style={{
          marginBottom: '24px',
          padding: '16px',
          background: 'rgba(20, 20, 40, 0.6)',
          borderRadius: '12px',
        }}
      >
        <div style={{ fontSize: '16px', color: COLORS.textGold, marginBottom: '12px' }}>
          属性別
        </div>
        {Object.entries(collectionStats.attributeStats).map(([attr, stats]) => (
          <ProgressBar
            key={attr}
            label={attr}
            current={stats.owned}
            total={stats.total}
            color={ATTRIBUTE_COLORS[attr]?.primary}
          />
        ))}
      </div>

      {/* 種類別進捗 */}
      <div
        style={{
          marginBottom: '24px',
          padding: '16px',
          background: 'rgba(20, 20, 40, 0.6)',
          borderRadius: '12px',
        }}
      >
        <div style={{ fontSize: '16px', color: COLORS.textGold, marginBottom: '12px' }}>
          種類別
        </div>
        {Object.entries(collectionStats.typeStats).map(([type, stats]) => (
          <ProgressBar key={type} label={type} current={stats.owned} total={stats.total} />
        ))}
      </div>

      {/* レアリティ別進捗 */}
      <div
        style={{
          padding: '16px',
          background: 'rgba(20, 20, 40, 0.6)',
          borderRadius: '12px',
        }}
      >
        <div style={{ fontSize: '16px', color: COLORS.textGold, marginBottom: '12px' }}>
          レアリティ別
        </div>
        {Object.entries(collectionStats.rarityStats).map(([rarity, stats]) => (
          <ProgressBar
            key={rarity}
            label={rarity}
            current={stats.owned}
            total={stats.total}
            color={RARITY_COLORS[rarity]}
          />
        ))}
      </div>
    </div>
  );
};

export default ProgressScreen;
